import { useContext, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
// Context
import { AlertNotificationContext } from "../contexts/AlertNotificationContext";
// Hooks
import { useCheckAPI } from "../hooks/useCheckAPI";
import useUser from "../hooks/useUser";
// State
import { useSaveStaffOrder } from "../hooks/useSaveStaffOrder";
// Components
import { ComponentAuthorisation } from "./ComponentAuthorisation";
import { ImageCarousel } from "./ImageCarousel";
import { OrderSummaryEditQty } from "./OrderSummaryEditQty";
import { OverEntitlementDialog } from "./OverEntitlementDialog";
import { DraftEntitlementDialog } from "./DraftEntitlementDialog";
// Utils
import { currencyFormatter } from "../utils/Utils";

// MUI
import { grey } from "@mui/material/colors";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Stack from "@mui/material/Stack";
import ListItemText from "@mui/material/ListItemText";
// Avatar handled by shared ProductImageAvatar component
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import { ProductImageAvatar } from "./ProductImageAvatar";

export const OrderItemsSummaryTable = ({
  order,
  person,
  items,
  staffRequest,
  orderType = "draft",
}) => {
  const queryClient = useQueryClient();
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const { user } = useUser();

  const [entitlementDialogOpen, setEntitlementDialogOpen] = useState(false);
  const [draftEntitlementDialogOpen, setDraftEntitlementDialogOpen] =
    useState(false);
  const [draftEntitlementMode, setDraftEntitlementMode] = useState("Warning");
  const [overEntitlementReason, setOverEntitlmentReason] = useState("");
  const [dataToSave, setDataToSave] = useState({});

  // Save order
  const {
    mutate: saveOrder,
    isSuccess: saveSuccess,
    data: saveData,
  } = useSaveStaffOrder();

  // Do this on save order success
  const saveSuccessFunc = () => {
    setAlertNotification({
      message: `Item Updated`,
      severity: "info",
    });
    queryClient.invalidateQueries({
      // queryKey: ["draftOrderStaff"],
      queryKey: ["draftOrderStaff", order.no, person.staffLineNo],
    });
    queryClient.invalidateQueries({
      queryKey: ["wardrobeCard", person.uniformId, person.no],
    });
  };

  // Do this on save order error
  const saveErrorFunc = () => {
    setAlertNotification({
      message: `Error: ${saveData.data.status}: ${saveData.data.statusText}`,
      severity: "error",
    });
  };

  // Hook to check the BC API for errors
  useCheckAPI(
    saveSuccess,
    saveData,
    saveData?.data?.status,
    saveSuccessFunc,
    saveErrorFunc,
  );

  const handleRemoveItem = (item) => {
    saveOrder({
      data: {
        filter: { orderNo: order.no, staffLineNo: person.staffLineNo },
        lines: [
          {
            lineNo: item.lineNo,
            productCode: item.productCode,
            colour: item.colour,
            size: item.size,
            fit: item.fit,
            quantity: 0,
          },
        ],
        reason: person.reasonCode,
        staffpoNo: person.staffpoNo,
      },
    });
  };

  const handleUpdateItemQty = (item, qty) => {
    const availalbeEntitlement =
      Number(item.entitlement) - Number(item.qtyIssued);
    const qtyDiff = Number(item.quantity) - Number(qty);
    const isOverEntitlement = qtyDiff + availalbeEntitlement < 0;

    const data = {
      data: {
        filter: { orderNo: order.no, staffLineNo: person.staffLineNo },
        lines: [
          {
            lineNo: item.lineNo,
            productCode: item.productCode,
            colour: item.colour,
            size: item.size,
            fit: item.fit,
            quantity: qty,
          },
        ],
        reason: person.reasonCode,
        staffpoNo: person.staffpoNo,
      },
    };

    setDataToSave(data);

    // Staff Request entitlement check (existing logic)
    if (staffRequest) {
      if (overEntitlementReason === "") {
        if (isOverEntitlement) {
          setEntitlementDialogOpen(true);
          return;
        }
      }
    }

    // Draft Order entitlement check (new logic)
    // Approvers can self-approve, so treat "Escalate" as "Warning" for them
    const overEntitlementAction = user?.overEntitlementAction || "No Action";
    const userType = user?.userType || "";
    const effectiveAction =
      userType === "Approver" && overEntitlementAction === "Escalate"
        ? "Warning"
        : overEntitlementAction;

    if (
      !staffRequest &&
      orderType === "draft" &&
      isOverEntitlement &&
      effectiveAction !== "No Action"
    ) {
      if (effectiveAction === "Block") {
        setDraftEntitlementMode("Block");
        setDraftEntitlementDialogOpen(true);
        return; // Block - do not save
      }
      if (effectiveAction === "Warning" || effectiveAction === "Escalate") {
        if (overEntitlementReason === "") {
          setDraftEntitlementMode(effectiveAction);
          setDraftEntitlementDialogOpen(true);
          return; // Wait for reason
        }
      }
    }

    saveOrder(data);
  };

  // Handler for DraftEntitlementDialog confirmation
  const handleDraftEntitlementConfirm = useCallback(
    (reason) => {
      const updatedData = {
        ...dataToSave,
        data: {
          ...dataToSave.data,
          lines: [
            {
              ...dataToSave.data.lines[0],
              overEntitlementReason: reason,
            },
          ],
        },
      };
      setDataToSave(updatedData);
      saveOrder(updatedData);
    },
    [dataToSave, saveOrder],
  );

  const [openImageCarousel, setOpenImageCarousel] = useState(false);
  const [imageCarouselProduct, setImageCarouselProduct] = useState("");

  // Handle Product Image Click
  const handleImageClick = (value) => {
    setOpenImageCarousel(true);
    setImageCarouselProduct(value);
  };

  // Handle Image Carousel Close
  const handleClose = () => {
    setOpenImageCarousel(false);
  };

  // Over Entitlement Reason Popover
  const OverEntitlementReason = ({ reason, entitlement, issued, lineQty }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    // const availalbeEntitlement = entitlement - (issued - lineQty);
    const availalbeEntitlement = entitlement - issued;

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "staff-details" : undefined;
    return (
      <div>
        <IconButton
          aria-describedby={id}
          aria-label="Details"
          color="secondary"
          onClick={handleClick}
        >
          <InfoIcon />
        </IconButton>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Paper sx={{ p: 2 }}>
            <Typography sx={{ p: 1 }}>
              Over entitlement reason: <strong>{reason}</strong>
            </Typography>
            <Typography sx={{ p: 1 }}>
              Entitlement is: <strong>{entitlement}</strong>
            </Typography>
            <Typography sx={{ p: 1 }}>
              Issued: <strong>{issued}</strong> (includes this order)
            </Typography>
            <Typography sx={{ p: 1 }}>
              Available entitlement is: <strong>{availalbeEntitlement}</strong>
            </Typography>
            <Typography sx={{ p: 1 }}>
              Available entitlement includes the quantity allocated across all
              orders, including Completed, Received, and Draft Orders.
            </Typography>
          </Paper>
        </Popover>
      </div>
    );
  };

  return (
    <>
      {items && items.length > 0 ? (
        <TableContainer component={Paper}>
          <Table aria-label="Summary Items" size="small">
            <TableHead
              sx={(theme) => ({
                backgroundColor: grey[300],
                ...theme.applyStyles("dark", { backgroundColor: grey[700] }),
              })}
            >
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Details</TableCell>
                <ComponentAuthorisation
                  allowedRoles={["allowentitlement", "staffRequestCreate"]}
                >
                  <TableCell align="center">Entitlement</TableCell>
                </ComponentAuthorisation>
                <TableCell>Quantity</TableCell>
                {!staffRequest && <TableCell>Price</TableCell>}
                {!staffRequest && <TableCell>Total</TableCell>}
                <TableCell align="center">Remove</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {items?.map((item) => (
                <TableRow
                  key={item.lineNo}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        justifyContent: "flex-start",
                        alignItems: "center",
                        py: 1,
                      }}
                    >
                      <IconButton
                        onClick={() => handleImageClick(item.productCode)}
                        sx={{ p: 0 }}
                      >
                        <ProductImageAvatar productCode={item.productCode} />
                      </IconButton>
                      <ListItemText
                        primary={item.productName}
                        secondary={
                          <Typography
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {item.productCode}
                          </Typography>
                        }
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25} sx={{ py: 0.5 }}>
                      {item.colourDescription && (
                        <Typography variant="body2">
                          {item.colourDescription}
                        </Typography>
                      )}
                      {item.sizeDescription && (
                        <Typography variant="body2">
                          {item.sizeDescription}
                        </Typography>
                      )}
                      {item.fitDescription && (
                        <Typography variant="body2">
                          {item.fitDescription}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <ComponentAuthorisation
                    allowedRoles={["allowentitlement", "staffRequestCreate"]}
                  >
                    <TableCell align="center">
                      {item.overEntitlementReason && (
                        <OverEntitlementReason
                          reason={item.overEntitlementReason}
                          entitlement={Number(item.entitlement)}
                          issued={Number(item.qtyIssued)}
                          lineQty={Number(item.quantity)}
                        />
                      )}
                    </TableCell>
                  </ComponentAuthorisation>

                  {/* <ComponentAuthorisation
                    allowedRoles={["allowentitlement", "staffRequestCreate"]}
                  >
                    <TableCell>
                      <Typography>{item.entitlement}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{item.qtyIssued}</Typography>
                    </TableCell>
                  </ComponentAuthorisation> */}
                  {/* {!staffRequest && (
                    <TableCell>
                      <Typography>{item.freeStockQty}</Typography>
                    </TableCell>
                  )} */}
                  {/* <TableCell>
                    <Typography>{item.overEntitlementReason}</Typography>
                  </TableCell> */}
                  {/* <TableCell>
                    <Typography>{item.leadTime}</Typography>
                  </TableCell> */}
                  {/* <TableCell>{QuantityEdit(item.quantity, true)}</TableCell> */}

                  <TableCell>
                    <OrderSummaryEditQty
                      item={item}
                      handleUpdateItemQty={handleUpdateItemQty}
                    />
                  </TableCell>

                  {/* <TableCell>
                    <Typography>{item.quantity}</Typography>
                  </TableCell> */}
                  {!staffRequest && (
                    <TableCell>
                      <Typography>
                        {currencyFormatter("GB", item.cost)}
                      </Typography>
                    </TableCell>
                  )}
                  {!staffRequest && (
                    <TableCell>
                      <Typography>
                        {currencyFormatter("GB", item.cost * item.quantity)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      maxWidth: 200,
                      margin: "auto",
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      aria-label="Remove"
                      color="error"
                      onClick={() => handleRemoveItem(item)}
                      // disabled={removeDisabled}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography>No items</Typography>
      )}
      <ImageCarousel
        open={openImageCarousel}
        productCode={imageCarouselProduct}
        handleClose={handleClose}
      />
      <OverEntitlementDialog
        open={entitlementDialogOpen}
        setOpen={setEntitlementDialogOpen}
        reason={overEntitlementReason}
        setReason={setOverEntitlmentReason}
        saveOrder={saveOrder}
        saveData={dataToSave}
        setDataToSave={setDataToSave}
        orderSummary
      />
      <DraftEntitlementDialog
        open={draftEntitlementDialogOpen}
        setOpen={setDraftEntitlementDialogOpen}
        reason={overEntitlementReason}
        setReason={setOverEntitlmentReason}
        onConfirm={handleDraftEntitlementConfirm}
        mode={draftEntitlementMode}
      />
    </>
  );
};
